import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { Eye, EyeOff, LogIn } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import jobLogo from "../images/Logo.svg";

const LoginPage = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const [userType, setUserType] = useState(
    query.get("type") === "institution" ? "institution" : "user"
  );

  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

  /* ================= VALIDATION ================= */

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
  });

  /* ================= LOGIN HANDLER ================= */

  const handleLogin = async (values, { setSubmitting, setErrors }) => {
    const loginUrl =
      userType === "user"
        ? `${REACT_APP_API_URL}/api/auth/login-user`
        : `${REACT_APP_API_URL}/api/auth/login-inst`;

    try {
      const response = await axios.post(loginUrl, values, {
        withCredentials: true,
      });

      const accessToken = response.data?.accessToken || response.data?.token;

      if (!accessToken) {
        setErrors({ general: "No token received from server." });
        return;
      }

      localStorage.setItem("token", accessToken);
      localStorage.setItem("refreshToken", response.data.refreshToken || "");
      localStorage.setItem("role", response.data.role);

      if (response.data.role === "admin") {
        navigate("/admin/dashboard");
      } else {
        navigate(userType === "user" ? "/" : "/InstitutionHomepage");
      }
    } catch (error) {
      const message = error.response?.data?.message;

      if (message === "User not found!") {
        setErrors({ email: message });
      } else if (message === "Invalid Credentials!") {
        setErrors({ password: message });
      } else {
        setErrors({
          general: message || "Login failed. Please try again.",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  /* ================= UI ================= */

  return (
    <div className="min-h-screen w-full font-['Inter',sans-serif] flex items-center justify-center p-4 md:p-8 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >

      {/* Decorative background shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)' }}
        />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)' }}
        />
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)' }}
        />
      </div>

      {/* Card */}
      <div className="relative w-full !max-w-lg bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8 z-10"
        style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.3)' }}
      >
        {/* Branding */}
        <div className="flex flex-col items-center mb-8">
          <img src={jobLogo} alt="Logo" className="h-16 mb-4 cursor-pointer" onClick={() => navigate("/")} />
          <h1 className="text-2xl font-bold text-gray-800 tracking-tight">Welcome Back</h1>
          <p className="text-gray-500 text-sm mt-1">Sign in to continue to your account</p>
        </div>

        {/* Toggle Buttons */}
        <div className="flex bg-gray-100 rounded-xl p-1 mb-8">
          <button
            type="button"
            onClick={() => setUserType("user")}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-300 border-none cursor-pointer ${userType === "user"
              ? "bg-white text-blue-600 shadow-md"
              : "bg-transparent text-gray-500 hover:text-gray-700"
              }`}
          >
            User
          </button>
          <button
            type="button"
            onClick={() => setUserType("institution")}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-300 border-none cursor-pointer ${userType === "institution"
              ? "bg-white text-blue-600 shadow-md"
              : "bg-transparent text-gray-500 hover:text-gray-700"
              }`}
          >
            Institution
          </button>
        </div>

        {/* Formik Form */}
        <Formik
          initialValues={{ email: "", password: "" }}
          validationSchema={validationSchema}
          onSubmit={handleLogin}
        >
          {({ isSubmitting, errors }) => (
            <Form>
              {/* General Error */}
              {errors.general && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4 text-center">
                  {errors.general}
                </div>
              )}

              {/* Email */}
              <div className="mb-5">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  Email Address
                </label>
                <Field
                  type="email"
                  name="email"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 text-sm placeholder-gray-400 outline-none transition-all duration-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100"
                  placeholder={
                    userType === "user"
                      ? "Enter your email"
                      : "Enter registered email"
                  }
                />
                <ErrorMessage
                  name="email"
                  component="div"
                  className="text-red-500 text-xs mt-1.5 ml-1"
                />
              </div>

              {/* Password */}
              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  Password
                </label>
                <div className="relative">
                  <Field
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="w-full px-4 py-3 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 text-sm placeholder-gray-400 outline-none transition-all duration-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100"
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <ErrorMessage
                  name="password"
                  component="div"
                  className="text-red-500 text-xs mt-1.5 ml-1"
                />
              </div>

              {/* Forgot Password */}
              <div className="text-right mb-6">
                <span
                  className="text-blue-600 text-sm font-medium cursor-pointer hover:text-blue-700 transition-colors"
                  onClick={() => navigate("/forgot-password")}
                >
                  Forgot Password?
                </span>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 text-white font-semibold text-sm rounded-xl border-none cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                <LogIn size={18} />
                {isSubmitting ? "Signing in..." : "Sign In"}
              </button>
            </Form>
          )}
        </Formik>

        {/* Register Link */}
        <div className="mt-6 text-center">
          <span className="text-sm text-gray-500">
            Don&apos;t have an account?{" "}
            <span
              className="text-blue-600 font-semibold cursor-pointer hover:text-blue-700 transition-colors"
              onClick={() =>
                navigate(userType === "user" ? "/user" : "/inst")
              }
            >
              Create Account
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;